/**
 * Roster tab: who is registered/waitlisted for this activity, live from RMS
 * via the Supergraph (browser-direct, GRMSB-785 pattern). Read-only.
 *
 * Answers the long-standing ask to see the waitlist without leaving Grimsby
 * (SCHDLMGMT-1905) and the unwaitlist-not-reflecting reports: this reads the
 * registration source of truth, not a synced copy.
 */
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Alert,
    Spinner,
    Table,
    StatusIndicator,
    Box,
    SpaceBetween,
    SegmentedControl,
} from '@amzn/awsui-components-react-v3';
import { RosterEntry } from '../../../../../common/interfaces/supergraph';
import { formatString } from '../../../../../common/utils/formatString';
import {
    loadRegistrationRoster,
    selectRegistrationRoster,
    selectIsRegistrationRosterLoading,
    selectRegistrationRosterError,
} from '../../../../store/slices/selectedActivitySlice';

const STATUS_DISPLAY: Record<
    string,
    {
        label: string;
        type: 'success' | 'warning' | 'error' | 'stopped' | 'pending';
    }
> = {
    REGISTERED: { label: 'Registered', type: 'success' },
    WAITLISTED: { label: 'Waitlisted', type: 'pending' },
    UNWAITLISTED: { label: 'Unwaitlisted', type: 'warning' },
    WITHDRAWN: { label: 'Withdrawn', type: 'stopped' },
    CANCELED: { label: 'Canceled', type: 'error' },
};

interface RosterTabProps {
    learningActivityId: string;
}

const RosterTab = ({ learningActivityId }: RosterTabProps) => {
    const dispatch = useDispatch();
    // Roster is cached in the store, so switching away from and back to this
    // tab reads the cached value instead of refetching. Keyed on the activity
    // id so navigating to a different activity loads its roster (the store is
    // cleared on navigation by getSelectedActivity).
    const roster = useSelector(selectRegistrationRoster);
    const isLoading = useSelector(selectIsRegistrationRosterLoading);
    const hasError = useSelector(selectRegistrationRosterError);
    const [statusFilter, setStatusFilter] = useState('ALL');

    useEffect(() => {
        // Only load once per activity: skip if the roster is already cached
        // (e.g. returning to the tab) unless a prior fetch failed.
        if (roster === null && !isLoading) {
            dispatch(loadRegistrationRoster());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, learningActivityId]);

    if (isLoading) {
        return <Spinner data-testid="RosterTabSpinner" size="large" />;
    }

    if (hasError || roster === null) {
        return (
            <Alert
                data-testid="RosterTabUnavailable"
                statusIconAriaLabel="Info"
            >
                The registration roster is currently unavailable.
            </Alert>
        );
    }

    const filtered =
        statusFilter === 'ALL'
            ? roster
            : roster.filter(
                  (entry) => entry.registrationStatus === statusFilter,
              );

    const waitlistedCount = roster.filter(
        (entry) => entry.registrationStatus === 'WAITLISTED',
    ).length;
    const registeredCount = roster.filter(
        (entry) => entry.registrationStatus === 'REGISTERED',
    ).length;

    return (
        <SpaceBetween size="m">
            <Box data-testid="RosterSummary">
                <StatusIndicator type="success">
                    Registered: {registeredCount}
                </StatusIndicator>
                <Box display="inline-block" margin={{ left: 'l' }}>
                    <StatusIndicator type="pending">
                        Waitlisted: {waitlistedCount}
                    </StatusIndicator>
                </Box>
            </Box>
            <SegmentedControl
                data-testid="RosterStatusFilter"
                selectedId={statusFilter}
                onChange={({ detail }) => setStatusFilter(detail.selectedId)}
                options={[
                    { text: 'All', id: 'ALL' },
                    { text: 'Registered', id: 'REGISTERED' },
                    { text: 'Waitlisted', id: 'WAITLISTED' },
                    { text: 'Withdrawn', id: 'WITHDRAWN' },
                ]}
            />
            <Table
                data-testid="RosterTable"
                columnDefinitions={[
                    {
                        id: 'learner',
                        header: 'Learner',
                        cell: (entry: RosterEntry) =>
                            formatString(
                                entry.learnerName || entry.learnerEmail,
                            ),
                    },
                    {
                        id: 'email',
                        header: 'Email',
                        cell: (entry: RosterEntry) =>
                            formatString(entry.learnerEmail),
                    },
                    {
                        id: 'status',
                        header: 'Status',
                        cell: (entry: RosterEntry) => {
                            const display =
                                STATUS_DISPLAY[entry.registrationStatus];
                            return display ? (
                                <StatusIndicator type={display.type}>
                                    {display.label}
                                </StatusIndicator>
                            ) : (
                                formatString(entry.registrationStatus)
                            );
                        },
                    },
                    {
                        id: 'organization',
                        header: 'Organization',
                        cell: (entry: RosterEntry) =>
                            formatString(entry.organizationName),
                    },
                ]}
                items={filtered}
                empty={
                    <Box textAlign="center">
                        No registrations for this activity.
                    </Box>
                }
            />
        </SpaceBetween>
    );
};

export default RosterTab;
